import { useGetAllStudents, useGetCaseTracker, useSyncCaseTrackerHubspot } from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Button from '@components/V4/Button';
import ChurnRiskBadge from '@components/V4/ChurnRisk/ChurnRiskBadge';
import Select from '@components/V4/Select/Select';
import Tabs from '@components/V4/Tabs/Tabs';
import { ExternalLinkIcon, RefreshIcon } from '@heroicons/react/outline';
import { useDebounce } from '@hooks';
import {
  EmptyState,
  FilterField,
  SectionHeading,
  TableShell,
} from '@modules/Admin/Reporting/common';
import DetailsModal from '@modules/Admin/components/DetailsModal/DetailsModal';
import ContractDetailsModal from '@modules/Admin/pages/Contracts/components/ContractDetailsModal';
import ContractFormModal from '@modules/Admin/pages/Contracts/components/ContractFormModal';
import {
  churnTone,
  contractStatusTone,
  formatDate,
  formatMoney,
  frequencyLabel,
  invoiceStatusTone,
} from '@modules/Admin/pages/Contracts/utils';
import { ChurnBand } from '@shared/clientTypes/DTOs/ChurnDTO';
import { ContractRowDTO } from '@shared/clientTypes/DTOs/ContractsDTO';
import {
  CLIENT_INVOICE_STATUS_LABELS,
  CONTRACT_CONTACT_FORM_LABELS,
  CONTRACT_STATUS_LABELS,
  ENROLLMENT_STATUS_TYPES,
  MEETING_STATUSES,
} from '@shared/constants';
import useToast from 'apps/agora/src/hooks/useToast';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import CaseJourney from './CaseJourney';

const headRow = 'border-b border-white/10 text-xs uppercase tracking-wide text-customGrey';
const bodyRow = 'border-b border-white/5 align-top last:border-b-0';
const leftHead = 'px-4 py-2.5 text-left font-semibold';
const rightHead = 'px-4 py-2.5 text-right font-semibold';
const leftCell = 'px-4 py-2.5 text-left text-customLight';
const rightCell = 'px-4 py-2.5 text-right tabular-nums text-customLight';

const Field = (props: { label: string; children: ReactNode; hint?: string; span?: boolean }) => (
  <div className={mergeClassNames('flex flex-col gap-0.5', props.span && 'col-span-2')}>
    <span className="text-[10px] font-semibold uppercase tracking-wide text-customGrey">
      {props.label}
    </span>
    <span className="text-sm text-white">{props.children}</span>
    {props.hint ? <span className="text-[10px] text-customGrey">{props.hint}</span> : null}
  </div>
);

const Card = (props: {
  title: string;
  description?: string;
  children: ReactNode;
  actions?: ReactNode;
}) => (
  <section className="flex flex-col gap-4 rounded-2xl bg-surfaceObject p-5">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <SectionHeading title={props.title} description={props.description} />
      {props.actions}
    </div>
    {props.children}
  </section>
);

const Source = (props: { children: ReactNode }) => (
  <span className="rounded bg-white/10 px-1.5 py-px text-[10px] font-semibold uppercase text-customGrey">
    {props.children}
  </span>
);

const dash = (value?: ReactNode) =>
  value === undefined || value === null || value === '' ? '—' : value;

const formatMaybeDate = (value?: string | null) =>
  value && moment(value).isValid() ? moment(value).format('DD.MM.YYYY') : '—';

const MS_PER_DAY = 86400000;

const formatDealValue = (name: string, value: unknown) => {
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return formatMaybeDate(value);
  const numeric = Number(value);
  if (/days/i.test(name) && Number.isFinite(numeric) && Math.abs(numeric) >= MS_PER_DAY) {
    return `${Math.round(numeric / MS_PER_DAY)} days`;
  }
  return String(value ?? '');
};

const SALES_AMOUNT_PATTERN =
  /sales.*amount|amount.*sales|contract.*(value|amount)|total.*(value|amount)/i;

const dealAmountOf = (deal: { amount?: number; properties?: Record<string, unknown> }) => {
  if (deal.amount) return deal.amount;
  for (const [name, value] of Object.entries(deal.properties ?? {})) {
    if (!SALES_AMOUNT_PATTERN.test(name)) continue;
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) return numeric;
  }
  return deal.amount;
};

const humanize = (name: string) =>
  name.replace(/_/g, ' ').replace(/^\w/, (character) => character.toUpperCase());

type CaseTab = 'case' | 'journey';

const useQuery = () => new URLSearchParams(useLocation().search);

const CONTACT_FORM_LABELS: Record<string, string> = CONTRACT_CONTACT_FORM_LABELS;

const CaseTracker = () => {
  const history = useHistory();
  const query = useQuery();
  const toast = useToast();
  const [studentId, setStudentId] = useState<string | undefined>(query.get('student') ?? undefined);
  const [studentSearch, setStudentSearch] = useState('');
  const debouncedSearch = useDebounce(studentSearch, 400);
  const [selectedContractId, setSelectedContractId] = useState<string>();
  const [detailsContractId, setDetailsContractId] = useState<string>();
  const [formContract, setFormContract] = useState<ContractRowDTO | null>();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [modalStudentId, setModalStudentId] = useState<string>();
  const [tab, setTab] = useState<CaseTab>('case');

  const { data: students, isLoading: isSearching } = useGetAllStudents({
    search: debouncedSearch,
    limit: 20,
  });

  const { mutate: syncHubspot, isLoading: isSyncingHubspot } = useSyncCaseTrackerHubspot(
    studentId ?? '',
    {
      onSuccess: (result) => {
        if (result.matched) {
          toast.success(
            `HubSpot deal synced: ${result.dealName ?? ''} (${result.stageLabel ?? ''})`
          );
        } else {
          toast.info(result.reason ?? 'No matching HubSpot deal.');
        }
        refetch();
      },
      onError: (error: any) =>
        toast.error(error?.response?.data?.error?.message ?? 'HubSpot sync failed.'),
    }
  );

  const { data, isLoading, isFetching, refetch } = useGetCaseTracker(studentId ?? '', {
    enabled: !!studentId,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!studentId) return;
    const params = new URLSearchParams();
    params.set('student', studentId);
    history.replace({ search: params.toString() });
  }, [studentId, history]);

  useEffect(() => {
    if (!data) return;
    if (
      !selectedContractId ||
      !data.contracts.some((row) => row.contractId === selectedContractId)
    ) {
      setSelectedContractId(data.contracts.find((row) => row.hasContract)?.contractId ?? undefined);
    }
  }, [data, selectedContractId]);

  const row = useMemo(
    () =>
      data?.contracts.find((item) => item.contractId === selectedContractId) ?? data?.contracts[0],
    [data, selectedContractId]
  );

  const studentOptions = useMemo(
    () =>
      (students ?? []).map((student) => ({
        value: student._id,
        label: `${student.fullName}${student.email ? ` · ${student.email}` : ''}`,
      })),
    [students]
  );

  const sessionsPaidTotal =
    row && row.summary.pricePerSession > 0
      ? Math.floor(row.summary.paidAmount / row.summary.pricePerSession)
      : 0;
  const doneNotPaid = row ? Math.max(0, row.sessions.done - sessionsPaidTotal) : 0;
  const installmentAmount =
    row && row.installmentsCount > 0 ? row.summary.totalAmount / row.installmentsCount : 0;

  const signedAmount = data?.hubspot.salesDeal?.amount || undefined;

  const dealProperties = data?.hubspot.deal?.properties
    ? Object.entries(data.hubspot.deal.properties).sort(([a], [b]) => a.localeCompare(b))
    : [];

  const openContractForm = (contract?: ContractRowDTO | null) => {
    setFormContract(contract ?? null);
    setIsFormOpen(true);
  };

  return (
    <div className="flex flex-col gap-8 p-8 font-raleway text-white">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold">Case Tracker</h1>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <FilterField width="w-full laptop:w-[480px]">
          <Select
            label="Student"
            variant="secondary"
            showSearch
            filterOption={() => true}
            searchValue={studentSearch}
            onSearch={setStudentSearch}
            onBlur={() => setStudentSearch('')}
            isLoading={isSearching}
            value={studentId}
            options={studentOptions}
            onSelect={(value) => {
              setStudentId(value ? String(value) : undefined);
              setSelectedContractId(undefined);
            }}
            placeholder="Type a student or parent name, or an email"
          />
        </FilterField>
        {studentId ? (
          <Button
            variant="secondary"
            buttonText={isSyncingHubspot ? 'Syncing HubSpot…' : 'Sync HubSpot deal'}
            disabled={isSyncingHubspot}
            onClick={() => syncHubspot({})}
          />
        ) : null}
        {studentId ? (
          <Button
            variant="secondary"
            buttonText="Refresh"
            icon={
              <RefreshIcon className={mergeClassNames('h-4 w-4', isFetching && 'animate-spin')} />
            }
            disabled={isFetching}
            onClick={() => refetch()}
          />
        ) : null}
      </div>

      {!studentId ? (
        <EmptyState>Pick a student to load their case.</EmptyState>
      ) : isLoading || !data ? (
        <div className="flex h-72 flex-col items-center justify-center gap-3 text-sm text-customGrey">
          <LoadingSpinner />
          <span>Collecting Agora, SmartBill and HubSpot data…</span>
        </div>
      ) : (
        <Tabs<CaseTab>
          tabs={[
            { label: 'Case', value: 'case' },
            { label: 'User journey', value: 'journey' },
          ]}
          activeTab={tab}
          onTabChange={setTab}
          renderContent={(current) =>
            current === 'journey' ? (
              <CaseJourney studentId={data.student._id} />
            ) : (
              <div className="flex flex-col gap-6">
                <Card
                  title={data.student.fullName}
                  description={[
                    data.student.graduationYear
                      ? `Graduation ${data.student.graduationYear}`
                      : undefined,
                    data.student.isDeactivated ? 'Deactivated account' : undefined,
                    data.student.createdAt
                      ? `In Agora since ${moment(data.student.createdAt).format('MMM YYYY')}`
                      : undefined,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  actions={
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="secondary"
                        buttonText="Open student profile"
                        onClick={() => setModalStudentId(data.student._id)}
                      />
                      {data.hubspot.contact.url ? (
                        <a
                          className="inline-flex items-center gap-1 rounded px-3 py-1 text-sm text-customPrimary hover:underline"
                          href={data.hubspot.contact.url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          HubSpot contact <ExternalLinkIcon className="h-4 w-4" />
                        </a>
                      ) : null}
                    </div>
                  }
                >
                  <div className="grid grid-cols-2 gap-x-6 gap-y-4 tablet:grid-cols-4 laptop:grid-cols-6">
                    <Field label="Client" hint="SmartBill client on the contract">
                      {dash(row?.client?.name ?? row?.parent.fullName)}
                    </Field>
                    <Field label="CIF / CNP" hint="SmartBill client">
                      {dash(row?.client?.vatCode)}
                    </Field>
                    <Field label="Parent name">{dash(data.student.parent.fullName)}</Field>
                    <Field label="Parent email">{dash(data.student.parent.email)}</Field>
                    <Field label="Parent phone">{dash(data.student.parent.phone)}</Field>
                    <Field label="Student phone">{dash(data.student.phone)}</Field>
                    <Field label="Strategist">{dash(data.student.strategist?.fullName)}</Field>
                    <Field label="Churn risk" hint="Agora churn score v3">
                      {data.churn ? (
                        <ChurnRiskBadge
                          score={data.churn.score}
                          band={data.churn.band as ChurnBand}
                        />
                      ) : (
                        '—'
                      )}
                    </Field>
                    <Field
                      label="Last contact"
                      hint="HubSpot calls, emails, meetings, notes + Agora log"
                    >
                      {data.hubspot.lastContact
                        ? `${formatMaybeDate(data.hubspot.lastContact.at)} · ${
                            CONTACT_FORM_LABELS[data.hubspot.lastContact.form] ??
                            humanize(data.hubspot.lastContact.form)
                          }`
                        : '—'}
                    </Field>
                    <Field label="Who contacted">
                      {dash(data.hubspot.lastContact?.by ?? data.hubspot.contact.owner)}
                    </Field>
                    <Field label="HubSpot lifecycle">
                      {dash(data.hubspot.contact.lifecycleStage)}
                      {data.hubspot.contact.leadStatus
                        ? ` · ${data.hubspot.contact.leadStatus}`
                        : ''}
                    </Field>
                    <Field label="Strategy meeting">
                      {data.meetings.find((meeting) => meeting.systemType === 'education-strategy')
                        ? formatMaybeDate(
                            data.meetings.find(
                              (meeting) => meeting.systemType === 'education-strategy'
                            )?.scheduledDate
                          )
                        : '—'}
                    </Field>
                  </div>
                </Card>

                <Card
                  title="Contract and payments"
                  description="From the Agora contract registry and SmartBill invoices. Edit case notes, contacts, addenda and invoices in the contract details."
                  actions={
                    <div className="flex flex-wrap items-center gap-2">
                      {data.contracts.filter((item) => item.hasContract).length > 1 ? (
                        <Select
                          variant="secondary"
                          className="!w-64"
                          value={selectedContractId}
                          options={data.contracts
                            .filter((item) => item.hasContract)
                            .map((item) => ({
                              value: item.contractId as string,
                              label: `Contract ${item.contractNumber ?? ''} · ${formatDate(
                                item.contractDate
                              )}`,
                            }))}
                          onSelect={(value) =>
                            setSelectedContractId(value ? String(value) : undefined)
                          }
                        />
                      ) : null}
                      {row?.hasContract ? (
                        <Button
                          buttonText="Contract details & notes"
                          onClick={() => setDetailsContractId(row.contractId as string)}
                        />
                      ) : (
                        <Button
                          buttonText="Create contract"
                          onClick={() => openContractForm(null)}
                        />
                      )}
                    </div>
                  }
                >
                  {!row?.hasContract ? (
                    <p className="text-sm text-customGrey">
                      No contract recorded in Agora for this student. Payment and installment data
                      below the HubSpot deal is all we have until one is created.
                    </p>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-4 tablet:grid-cols-4 laptop:grid-cols-6">
                        <Field label="Contract number">{dash(row.contractNumber)}</Field>
                        <Field label="Contract date">{formatDate(row.contractDate)}</Field>
                        <Field label="Contract">
                          {row.contractUrl ? (
                            <a
                              className="text-customPrimary hover:underline"
                              href={row.contractUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Open document
                            </a>
                          ) : (
                            '—'
                          )}
                        </Field>
                        <Field label="Status">
                          <span
                            className={mergeClassNames(
                              'rounded-full px-2 py-0.5 text-xs font-semibold',
                              contractStatusTone(row.status)
                            )}
                          >
                            {CONTRACT_STATUS_LABELS[row.status] ?? row.status}
                          </span>
                        </Field>
                        <Field
                          label="Contract amount"
                          hint={
                            row.summary.totalAmount
                              ? `Agora contract registry${
                                  signedAmount && signedAmount !== row.summary.totalAmount
                                    ? ` · HubSpot closed-won deal says ${formatMoney(
                                        signedAmount,
                                        row.currency
                                      )}`
                                    : ''
                                }`
                              : signedAmount
                              ? 'HubSpot Sales Pipeline, Closed won; no contract in Agora yet'
                              : 'Agora contract registry; no closed-won HubSpot deal synced'
                          }
                        >
                          {formatMoney(row.summary.totalAmount || signedAmount || 0, row.currency)}
                        </Field>
                        <Field
                          label="Price / session"
                          hint={
                            !row.summary.totalAmount &&
                            signedAmount &&
                            row.summary.sessionsInContract
                              ? 'HubSpot closed-won amount ÷ sessions in contract'
                              : undefined
                          }
                        >
                          {formatMoney(
                            row.summary.pricePerSession ||
                              (signedAmount && row.summary.sessionsInContract
                                ? signedAmount / row.summary.sessionsInContract
                                : 0),
                            row.currency
                          )}
                        </Field>
                        <Field label="Installments">{row.installmentsCount}</Field>
                        <Field label="Amount per installment">
                          {formatMoney(installmentAmount, row.currency)}
                        </Field>
                        <Field label="Frequency">{frequencyLabel(row)}</Field>
                        <Field label="Installments paid">{row.summary.installmentsPaid}</Field>
                        <Field label="Installments remaining">
                          {row.summary.installmentsRemaining}
                        </Field>
                        <Field label="Paid so far">
                          {formatMoney(row.summary.paidAmount, row.currency)}
                        </Field>
                        <Field label="Remaining to be paid">
                          {formatMoney(row.summary.remainingAmount, row.currency)}
                        </Field>
                        <Field label="Next due date">{formatDate(row.summary.nextDueDate)}</Field>
                        <Field label="Days overdue">
                          <span className={row.summary.overdueDays > 0 ? 'text-customRed' : ''}>
                            {row.summary.overdueDays}
                          </span>
                        </Field>
                        <Field label="Addenda">
                          {row.addenda.length
                            ? row.addenda.map((addendum) => (
                                <div key={addendum._id} className="text-xs">
                                  {formatDate(addendum.date)} · +{addendum.sessions} sessions ·{' '}
                                  {formatMoney(addendum.amount, row.currency)}
                                  {addendum.description ? ` · ${addendum.description}` : ''}
                                </div>
                              ))
                            : 'No'}
                        </Field>
                        <Field label="Payment promise">
                          {row.paymentPromise
                            ? `${formatMoney(
                                row.paymentPromise.amount,
                                row.currency
                              )} by ${formatDate(row.paymentPromise.date)}`
                            : '—'}
                        </Field>
                        <Field label="Final status">
                          {row.finalStatusOverride
                            ? CONTRACT_STATUS_LABELS[row.finalStatusOverride] ??
                              row.finalStatusOverride
                            : '—'}
                        </Field>
                      </div>

                      <div className="grid grid-cols-1 gap-4 laptop:grid-cols-3">
                        <Field label="Case context" span>
                          <span className="whitespace-pre-wrap text-sm text-customLight">
                            {dash(row.caseContext)}
                          </span>
                        </Field>
                        <Field label="Actions">
                          <span className="whitespace-pre-wrap text-sm text-customLight">
                            {dash(row.actions)}
                          </span>
                        </Field>
                        <Field label="Possible retention solution" span>
                          <span className="whitespace-pre-wrap text-sm text-customLight">
                            {dash(row.retentionSolution)}
                          </span>
                        </Field>
                        <Field label="Observations about sessions">
                          <span className="whitespace-pre-wrap text-sm text-customLight">
                            {dash(row.sessionsNotes)}
                            {row.sessions.autoNotes.length ? (
                              <ul className="mt-1 list-disc pl-4 text-xs text-customGold">
                                {row.sessions.autoNotes.map((note) => (
                                  <li key={note}>{note}</li>
                                ))}
                              </ul>
                            ) : null}
                          </span>
                        </Field>
                      </div>

                      <TableShell minWidth="min-w-[720px]">
                        <thead>
                          <tr className={headRow}>
                            <th className={leftHead}>Installment</th>
                            <th className={rightHead}>Amount</th>
                            <th className={leftHead}>Due</th>
                            <th className={leftHead}>Invoice</th>
                            <th className={leftHead}>Issued</th>
                            <th className={rightHead}>Total</th>
                            <th className={rightHead}>Unpaid</th>
                            <th className={leftHead}>Status</th>
                            <th className={rightHead}>Days late</th>
                          </tr>
                        </thead>
                        <tbody>
                          {row.installments.map((installment) => (
                            <tr key={installment.index} className={bodyRow}>
                              <td className={leftCell}>
                                #{installment.index}{' '}
                                {installment.paid ? (
                                  <span className="text-customGreen">paid</span>
                                ) : null}
                              </td>
                              <td className={rightCell}>
                                {formatMoney(installment.amount, row.currency)}
                              </td>
                              <td className={leftCell}>{formatDate(installment.dueDate)}</td>
                              <td className={leftCell}>
                                {installment.invoice
                                  ? `${installment.invoice.series}${installment.invoice.number}`
                                  : '—'}
                              </td>
                              <td className={leftCell}>
                                {formatDate(installment.invoice?.issueDate)}
                              </td>
                              <td className={rightCell}>
                                {installment.invoice
                                  ? formatMoney(
                                      installment.invoice.totalAmount,
                                      installment.invoice.currency
                                    )
                                  : '—'}
                              </td>
                              <td className={rightCell}>
                                {installment.invoice
                                  ? formatMoney(
                                      installment.invoice.unpaidAmount,
                                      installment.invoice.currency
                                    )
                                  : '—'}
                              </td>
                              <td className={leftCell}>
                                {installment.invoice ? (
                                  <span
                                    className={mergeClassNames(
                                      'rounded-full px-2 py-0.5 text-xs font-semibold',
                                      invoiceStatusTone(installment.invoice.status)
                                    )}
                                  >
                                    {CLIENT_INVOICE_STATUS_LABELS[installment.invoice.status] ??
                                      installment.invoice.status}
                                  </span>
                                ) : (
                                  <span className="text-customGrey">not invoiced</span>
                                )}
                              </td>
                              <td
                                className={mergeClassNames(
                                  rightCell,
                                  installment.invoice?.overdueDays ? 'text-customRed' : ''
                                )}
                              >
                                {installment.invoice?.overdueDays ?? '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </TableShell>
                    </>
                  )}
                </Card>

                <Card
                  title="Sessions: paid versus done"
                  description="Sessions from Agora enrollments priced at the contract's price per session. Paid sessions = amount paid so far divided by that price."
                >
                  <div className="grid grid-cols-2 gap-x-6 gap-y-4 tablet:grid-cols-4 laptop:grid-cols-5">
                    <Field label="Sessions in contract">
                      {dash(row?.summary.sessionsInContract)}
                    </Field>
                    <Field label="Sessions in Agora">{dash(row?.sessions.inAgora)}</Field>
                    <Field label="Sessions done">{dash(row?.sessions.done)}</Field>
                    <Field label="Value of sessions done">
                      {row ? formatMoney(row.sessions.valueDone, row.currency) : '—'}
                    </Field>
                    <Field label="Sessions covered by payments">{sessionsPaidTotal}</Field>
                    <Field label="Done but not paid">
                      <span className={doneNotPaid ? 'text-customRed' : ''}>{doneNotPaid}</span>
                    </Field>
                    <Field label="Value done but not paid">
                      {row
                        ? formatMoney(doneNotPaid * row.summary.pricePerSession, row.currency)
                        : '—'}
                    </Field>
                    <Field label="Paid but not done">{dash(row?.sessions.paidNotDone)}</Field>
                    <Field label="Value paid but not done">
                      {row ? formatMoney(row.sessions.valuePaidNotDone, row.currency) : '—'}
                    </Field>
                  </div>

                  {data.enrollments.length ? (
                    <TableShell minWidth="min-w-[720px]">
                      <thead>
                        <tr className={headRow}>
                          <th className={leftHead}>Course</th>
                          <th className={leftHead}>Status</th>
                          <th className={leftHead}>Mentor</th>
                          <th className={leftHead}>Started</th>
                          <th className={rightHead}>Sessions</th>
                          <th className={rightHead}>Done</th>
                          <th className={rightHead}>Scheduled</th>
                          <th className={leftHead}>Last</th>
                          <th className={leftHead}>Next</th>
                          <th className={leftHead}>Contract</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.enrollments.map((enrollment) => (
                          <tr key={enrollment._id} className={bodyRow}>
                            <td className={leftCell}>
                              <span className="inline-flex items-center gap-2">
                                <span
                                  className="h-2.5 w-2.5 rounded-full"
                                  style={{ backgroundColor: enrollment.courseColor ?? '#666' }}
                                />
                                {enrollment.courseTitle}
                              </span>
                            </td>
                            <td className={leftCell}>
                              {enrollment.status !== undefined
                                ? ENROLLMENT_STATUS_TYPES[enrollment.status]
                                : '—'}
                            </td>
                            <td className={leftCell}>{dash(enrollment.mentor)}</td>
                            <td className={leftCell}>{formatMaybeDate(enrollment.startDate)}</td>
                            <td className={rightCell}>{enrollment.sessionsCount}</td>
                            <td className={rightCell}>{enrollment.sessionsDone}</td>
                            <td className={rightCell}>{enrollment.sessionsScheduled}</td>
                            <td className={leftCell}>
                              {formatMaybeDate(enrollment.lastSessionAt)}
                            </td>
                            <td className={leftCell}>
                              {formatMaybeDate(enrollment.nextSessionAt)}
                            </td>
                            <td className={leftCell}>
                              {enrollment.contractIds.length ? (
                                <span className="text-customGreen">linked</span>
                              ) : (
                                <span className="text-customGold">not linked</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </TableShell>
                  ) : (
                    <p className="text-sm text-customGrey">No enrollments in Agora.</p>
                  )}
                </Card>

                <div className="grid grid-cols-1 gap-6 laptop:grid-cols-2">
                  <Card
                    title="HubSpot deals"
                    description={
                      data.hubspot.deal
                        ? `From the Contracts Pipeline, synced ${moment(
                            data.hubspot.deal.syncedAt
                          ).fromNow()}.`
                        : 'No deal from the Contracts Pipeline is linked to this student yet. Run "Sync HubSpot contracts" from the Students page if one exists.'
                    }
                  >
                    {data.hubspot.salesDeal ? (
                      <div className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-xl bg-customGreen/5 p-3">
                        <Field label="Signed contract (Sales Pipeline)" span>
                          {dash(data.hubspot.salesDeal.dealName)}
                        </Field>
                        <Field label="Stage">{dash(data.hubspot.salesDeal.stageLabel)}</Field>
                        <Field label="Amount" hint="Authoritative contract value">
                          {data.hubspot.salesDeal.amount ?? '—'}
                        </Field>
                        <Field label="Closed">
                          {formatMaybeDate(data.hubspot.salesDeal.closeDate)}
                        </Field>
                        <Field label="Synced">
                          {moment(data.hubspot.salesDeal.syncedAt).fromNow()}
                        </Field>
                      </div>
                    ) : (
                      <p className="text-sm text-customGold">
                        No closed-won deal in the Sales Pipeline for this student, so the contract
                        amount below comes from the Contracts Pipeline or the Agora registry.
                      </p>
                    )}
                    {data.hubspot.deal ? (
                      <div className="flex flex-col gap-3">
                        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                          <Field label="Contracts Pipeline deal">
                            {dash(data.hubspot.deal.dealName)}
                          </Field>
                          <Field label="Stage">{dash(data.hubspot.deal.stageLabel)}</Field>
                          <Field label="Amount">{dealAmountOf(data.hubspot.deal) ?? '—'}</Field>
                          <Field label="Close date">
                            {formatMaybeDate(data.hubspot.deal.closeDate)}
                          </Field>
                        </div>
                        {dealProperties.length ? (
                          <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 text-sm tablet:grid-cols-2">
                            {dealProperties.map(([name, value]) => (
                              <div
                                key={name}
                                className="flex justify-between gap-3 border-b border-white/5 py-1"
                              >
                                <dt className="text-customGrey">{humanize(name)}</dt>
                                <dd className="text-right text-white">
                                  {formatDealValue(name, value)}
                                </dd>
                              </div>
                            ))}
                          </dl>
                        ) : null}
                      </div>
                    ) : (
                      <p className="text-sm text-customGrey">
                        HubSpot contact: {data.hubspot.contact.status}
                        {data.hubspot.contact.reason ? ` (${data.hubspot.contact.reason})` : ''}
                      </p>
                    )}
                  </Card>

                  <Card
                    title="Recent contacts"
                    description="Latest HubSpot calls, emails, meetings and notes, plus contacts logged on the Agora contract."
                  >
                    {data.hubspot.recentContacts.length ? (
                      <ul className="flex flex-col gap-2">
                        {data.hubspot.recentContacts.map((contact, index) => (
                          <li
                            key={`${contact.at}-${index}`}
                            className="flex flex-col gap-0.5 text-sm"
                          >
                            <span className="flex items-center gap-2">
                              <span className="text-customGrey">{formatMaybeDate(contact.at)}</span>
                              <Source>
                                {CONTACT_FORM_LABELS[contact.form] ?? humanize(contact.form)}
                              </Source>
                              <Source>{contact.source}</Source>
                              {contact.url ? (
                                <a
                                  className="text-customPrimary hover:underline"
                                  href={contact.url}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {contact.title}
                                </a>
                              ) : (
                                <span className="text-white">{contact.title}</span>
                              )}
                            </span>
                            {contact.detail ? (
                              <span className="pl-1 text-xs text-customGrey">{contact.detail}</span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-customGrey">
                        No contacts found.{' '}
                        {data.hubspot.contact.status !== 'found'
                          ? `HubSpot contact: ${data.hubspot.contact.status}.`
                          : ''}
                      </p>
                    )}
                  </Card>
                </div>

                <div className="grid grid-cols-1 gap-6 laptop:grid-cols-2">
                  <Card
                    title="Strategy and onboarding meetings"
                    description="System meetings in Agora with this student."
                  >
                    {data.meetings.length ? (
                      <ul className="flex flex-col gap-1.5 text-sm">
                        {data.meetings.map((meeting) => (
                          <li key={meeting._id} className="flex flex-wrap items-center gap-2">
                            <span className="text-customGrey">
                              {formatMaybeDate(meeting.scheduledDate)}
                            </span>
                            <span className="text-white">
                              {humanize(meeting.systemType ?? 'meeting')}
                            </span>
                            {meeting.reason ? <Source>{humanize(meeting.reason)}</Source> : null}
                            <span className="text-customGrey">
                              {MEETING_STATUSES[meeting.status] ?? ''}
                            </span>
                            {meeting.mentor ? (
                              <span className="text-customGrey">with {meeting.mentor}</span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-customGrey">No strategy or onboarding meetings.</p>
                    )}
                  </Card>

                  <Card title="Churn drivers" description="Why the churn score is what it is.">
                    {data.churn?.drivers.length ? (
                      <ul className="flex flex-col gap-1.5 text-sm">
                        {data.churn.drivers.map((driver, index) => (
                          <li key={index} className="flex flex-col">
                            <span
                              className={churnTone(data.churn?.band as 'low' | 'medium' | 'high')}
                            >
                              {driver.label}
                            </span>
                            {driver.detail ? (
                              <span className="text-xs text-customGrey">{driver.detail}</span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-customGrey">
                        No churn score computed for this student.
                      </p>
                    )}
                  </Card>
                </div>
              </div>
            )
          }
        />
      )}

      {detailsContractId ? (
        <ContractDetailsModal
          contractId={detailsContractId}
          isOpen={true}
          onClose={() => {
            setDetailsContractId(undefined);
            refetch();
          }}
          onEdit={(contract) => {
            setDetailsContractId(undefined);
            openContractForm(contract);
          }}
          onOpenStudent={(id) => setModalStudentId(id)}
        />
      ) : null}

      {isFormOpen && data ? (
        <ContractFormModal
          isOpen={true}
          contract={formContract}
          presetStudent={{ _id: data.student._id, fullName: data.student.fullName }}
          onClose={() => {
            setIsFormOpen(false);
            setFormContract(undefined);
            refetch();
          }}
        />
      ) : null}

      {modalStudentId ? (
        <DetailsModal
          page="student"
          studentId={modalStudentId}
          isOpen={true}
          onClose={() => setModalStudentId(undefined)}
        />
      ) : null}
    </div>
  );
};

export default CaseTracker;
